<template>
  <c-dropdown
    v-model:value="open"
    class="c-select"
    :title="searchPlaceholder"
    :horizontal-alignment="horizontalAlignment"
    :open-to-top="openToTop"
    :overlapping="overlapping"
    :match-trigger-width="matchTriggerWidth"
  >
    <c-focusable
      :style="triggerStyle"
      :show-border="showBorder"
      has-tags
      wrap-content
    >
      <c-flex-layout :style="{ width: '100%' }">
        <template #left>
          <c-tag
            v-if="value.length === 0"
            :style="{ ...selectedTagStyle, marginLeft: '-6px' }"
            :title="placeholder"
            color="empty"
          />
          <c-tag
            v-for="(val,i) in calculatedValue"
            :key="i"
            :title="val.text"
            :icon="val.icon"
            :color="val.color"
            :style="selectedTagStyle"
            max-width="15rem"
          />
        </template>
        <template
          v-if="showBorder"
          #right
        >
          <c-icon
            type="down"
            size="12"
            :color="$colors.grey.lighten1"
            :style="{ marginLeft: 'auto', marginTop: '-.5rem' }"
          />
        </template>
      </c-flex-layout>
    </c-focusable>
    <template #content>
      <c-card
        list
        :style="cardStyle"
      >
        <c-scroller
          v-if="showSearch || overlapping"
          horizontal
          :style="searchWrapperStyle"
        >
          <template
            v-if="overlapping"
          >
            <c-tag
              v-for="(val, j) in calculatedValue"
              :key="j"
              :title="val.text"
              :color="val.color"
              :icon="val.icon"
              :style="selectedTagStyle"
              max-width="15rem"
              :closable="!valueRequired"
              @close:click.stop="select(val)"
            />
          </template>
          <div :style="inputWrapperStyle">
            <input
              ref="searchField"
              v-model="search"
              class="_search-field"
              :placeholder="value.length === 0 ? searchPlaceholder : ''"
              :style="searchStyle"
            >
          </div>
        </c-scroller>
        <c-scroller
          vertical
          :style="{ marginTop: '.4rem', marginBottom: '.4rem' }"
        >
          <div
            v-if="showSelectPlaceholder || overlapping"
            :style="selectPlaceholderStyle"
          >
            {{ selectPlaceholder }}
          </div>
          <c-focusable
            v-for="(option, k) in filteredOptions"
            :key="k"
            size="small"
            @click="select(option)"
          >
            <c-tag
              :title="option.text"
              :color="option.color"
              :icon="option.icon"
              :style="multiple ? selectedTagStyle : {}"
              max-width="15rem"
            />
          </c-focusable>
          <c-focusable
            v-if="canCreateOption && search !== '' && !exactMatch"
            :style="{ marginBottom: 0, borderRadius: 0 }"
            size="small"
            @click="createOption"
          >
            Create
            <c-tag
              :title="search"
              :color="randomColor"
              max-width="15rem"
              :style="{ marginLeft: '.6rem' }"
            />
          </c-focusable>
        </c-scroller>
      </c-card>
    </template>
  </c-dropdown>
</template>

<script>
import CCard from 'shared/components/CCard.vue';
import CDropdown from 'shared/components/CDropdown.vue';
import CFlexLayout from 'shared/components/CFlexLayout.vue';
import CFocusable from 'shared/components/CFocusable.vue';
import CIcon from 'shared/components/CIcon.vue';
import CScroller from 'shared/components/CScroller.vue';
import CTag from 'shared/components/CTag.vue';
import { notionColors } from 'shared/services/notion-colors';

export default {
  name: 'CSelect',
  props: {
    value: {
      type: Array,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    valueRequired: {
      type: Boolean,
      default: false,
    },
    showBorder: {
      type: Boolean,
      default: false,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: 'Empty',
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...',
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    selectPlaceholder: {
      type: String,
      default: 'Select an item',
    },
    showSelectPlaceholder: {
      type: Boolean,
      default: false,
    },
    horizontalAlignment: {
      type: String,
      default: 'center',
    },
    openToTop: {
      type: Boolean,
      default: false,
    },
    overlapping: {
      type: Boolean,
      default: false,
    },
    matchTriggerWidth: {
      type: Boolean,
      default: false,
    },
    canCreateOption: {
      type: Boolean,
      default: false,
    },
  },
  components: { CFlexLayout, CFocusable, CDropdown, CCard, CTag, CScroller, CIcon },
  data() {
    return {
      open: false,
      search: '',
      recomputeColor: 0,
      searchWrapperStyle: {
        display: 'flex',
        flexWrap: 'wrap',
        padding: '6px 8px 1px',
        background: 'rgba(242, 241, 238, 0.6)',
        boxShadow: 'rgb(55 53 47 / 16%) 0px -1px inset',
        minHeight: '25px',
      },
      triggerStyle: {
        minHeight: '3.2rem',
        minWidth: '80px',
        maxWidth: 'calc(100vw - 24px)',
      },
      searchStyle: {
        fontSize: '14px',
        lineHeight: 'inherit',
        border: 'none',
        background: 'none',
        width: '100%',
        display: 'block',
        resize: 'none',
        padding: '0px',
        height: '20px',
        outline: 'none',
      },
      cardStyle: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 'calc(100vw - 24px)',
        height: '100%',
        maxHeight: '70vh',
        width: '100%',
      },
      inputWrapperStyle: {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        padding: '0px',
        width: 'auto',
        background: 'transparent',
        fontSize: 'inherit',
        lineHeight: '20px',
        flex: '1 1 60px',
        minWidth: '60px',
        margin: '0px 6px 6px 0px',
        borderTopLeftRadius: '3px',
        borderTopRightRadius: '3px',
      },
    };
  },
  computed: {
    showDeleteButtonOnTag() {
      return !this.valueRequired || this.value.length <= 1;
    },
    selectedTagStyle() {
      return { margin: '0px 4px 4px 0px' };
    },
    calculatedValue() {
      return this.value.map((v) => {
        const opt = this.options.find((o) => o.value === v);
        if (opt === undefined) {
          return {};
        }
        return opt;
      });
    },
    exactMatch() {
      return this.options.some((o) => o.text === this.search);
    },
    randomColor() {
      const rand = (Math.random() * notionColors.length) * (this.recomputeColor / this.recomputeColor);
      return notionColors[Math.round(rand)];
    },
    filteredOptions() {
      return this.options.filter((o) => this.search === '' || o.text.toLowerCase().indexOf(this.search.toLowerCase()) > -1);
    },
    selectPlaceholderStyle() {
      return {
        display: 'flex',
        paddingLeft: '10px',
        paddingRight: '14px',
        marginTop: '6px',
        marginBottom: '4px',
        color: this.$colors.grey.lighten1,
        fontSize: '12px',
        fontWeight: '500',
        lineHeight: '120%',
        userSelect: 'none',
      };
    },
  },
  emits: ['update:value', 'option-created'],
  methods: {
    createOption() {
      const option = { text: this.search, value: this.search, color: this.randomColor };
      this.$emit('option-created', option);
      this.recomputeColor += 1;
      this.search = '';
      return this.select(option);
    },
    close() {
      this.open = false;
    },
    select(option) {
      this.search = '';
      if (this.value.some((v) => v === option.value)) {
        if (this.value.length === 1 && this.valueRequired) {
          return;
        }
        this.$emit('update:value', this.value.filter((v) => v !== option.value));
        this.focusSearch();
        return;
      }
      if (!this.multiple) {
        this.$emit('update:value', [option.value]);
        this.close();
        return;
      }
      this.$emit('update:value', [...this.value, option.value]);
      this.focusSearch();
    },
    focusSearch() {
      if (this.$refs.searchField === undefined || this.$refs.searchField === null) {
        return;
      }
      this.$refs.searchField.focus();
    },
  },
  watch: {
    open(newVal) {
      if (!newVal) {
        return;
      }

      this.$nextTick(() => {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.focusSearch();
          });
        });
      });
    },
  },
};
</script>

<style
    scoped
    lang="scss"
    type="text/scss"
>
  ._search-field::placeholder {
    color: map_get($grey, 'lighten-2'),
  }
</style>
